import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBriefcase,
    faEnvelope,
    faFileLines,
    faPhone,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';

import {
    applicantMatchesLocationFilter,
    formatHiringLocationPrimary,
    formatHiringLocationSecondary,
    getApplicantInitials,
    getHiringLocationKey,
} from './hiringMockData';
import { fetchJobProfilesForCurrentEntity } from './jobProfileApi';
import { isSupermarketContext } from './entityContext';

import HiringLocationAddressFilter from './HiringLocationAddressFilter';
import HiringRoleMultiSelect from './HiringRoleMultiSelect';
import CollapsibleFilters from './CollapsibleFilters';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';

import './css/Hiring.css';
import './css/Inventory.css';
import './css/Menu.css';
import './css/Purchase.css';
import './css/OrdersTablePagination.css';
import './css/UserManagement.css';

const COL_COUNT = 8;

function applicantMatchesSearch(applicant, query) {
    const q = String(query ?? '').trim().toLowerCase();
    if (!q) return true;
    const fields = [
        applicant.name,
        applicant.role,
        applicant.phone,
        applicant.email,
        applicant.skills,
        applicant.status,
    ];
    return fields.some((v) => v != null && String(v).toLowerCase().includes(q));
}

function getApplicantSortValue(row, sortKey) {
    switch (sortKey) {
        case 'name':
            return str(row.name);
        case 'role':
            return str(row.role);
        case 'locations':
            return Array.isArray(row.preferredLocations) ? row.preferredLocations.length : 0;
        case 'status':
            return str(row.status);
        case 'experience':
            return Number.parseFloat(row.experienceYears) || 0;
        case 'phone':
            return str(row.phone);
        case 'email':
            return str(row.email);
        case 'resume':
            return str(row.resumeFile);
        default:
            return '';
    }
}

function HiringApplicantsTable({
    applicants,
    loading,
    searchQuery,
    onSearchChange,
}) {
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [locationFilter, setLocationFilter] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [applicantsSort, setApplicantsSort] = useState(null);
    const [locationsModalApplicant, setLocationsModalApplicant] = useState(null);

    const roles = useMemo(() => {
        const set = new Set(applicants.map((a) => a.role).filter(Boolean));
        return Array.from(set).sort();
    }, [applicants]);

    const filtered = useMemo(() => {
        return applicants.filter((a) => {
            if (!applicantMatchesSearch(a, searchQuery)) return false;
            if (selectedRoles.length > 0 && !selectedRoles.includes(a.role)) {
                return false;
            }
            return applicantMatchesLocationFilter(a, locationFilter);
        });
    }, [applicants, searchQuery, selectedRoles, locationFilter]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, selectedRoles, locationFilter]);

    const sortedApplicants = useMemo(
        () => sortList(filtered, applicantsSort, getApplicantSortValue),
        [filtered, applicantsSort]
    );

    const { handleSortAsc: handleApplicantsSortAsc, handleSortDesc: handleApplicantsSortDesc } =
        buildSortHandlers(setApplicantsSort, () => setPage(1));

    const totalPages = Math.max(1, Math.ceil(sortedApplicants.length / pageSize));
    const pageSafe = Math.min(Math.max(1, page), totalPages);

    useEffect(() => {
        setPage((p) => Math.min(p, totalPages));
    }, [totalPages, pageSize]);

    const pageStart = (pageSafe - 1) * pageSize;
    const pagedApplicants = sortedApplicants.slice(pageStart, pageStart + pageSize);

    return (
        <>
            <div className="purchase-content-card hiring-content-card">
                <div className="inventory-list-toolbar menu-toolbar hiring-list-toolbar">
                    <div className="menu-toolbar-row hiring-toolbar-row">
                        <div className="menu-food-filter-wrap hiring-search-wrap">
                            <input
                                type="text"
                                inputMode="search"
                                autoComplete="off"
                                className="menu-food-filter-input"
                                placeholder="Search candidates…"
                                value={searchQuery}
                                onChange={(e) => onSearchChange(e.target.value)}
                                aria-label="Search candidates"
                                role="searchbox"
                            />
                            {searchQuery.trim() !== '' ? (
                                <button
                                    type="button"
                                    className="menu-food-filter-clear"
                                    aria-label="Clear search"
                                    onClick={() => onSearchChange('')}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            ) : null}
                        </div>
                        <CollapsibleFilters
                            activeCount={
                                (locationFilter ? 1 : 0) +
                                ((selectedRoles?.length || 0) > 0 ? 1 : 0)
                            }
                        >
                            <HiringLocationAddressFilter
                                value={locationFilter}
                                onChange={setLocationFilter}
                                placeholder="Search city…"
                            />
                            <HiringRoleMultiSelect
                                roles={roles}
                                selectedRoles={selectedRoles}
                                onChange={setSelectedRoles}
                                placeholder="All roles"
                                searchPlaceholder="Search roles..."
                            />
                        </CollapsibleFilters>
                    </div>
                </div>

                <div className="menu-table-wrapper hiring-table-wrap">
                    <table className="menu-table hiring-applicants-table">
                        <thead>
                            <tr>
                                <OrdersSortableTh label="Applicant" sortKey="name" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Role" sortKey="role" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Preferred locations" sortKey="locations" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Status" sortKey="status" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Experience" sortKey="experience" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Phone" sortKey="phone" className="table-col-mobile" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Email" sortKey="email" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                                <OrdersSortableTh label="Resume" sortKey="resume" activeSortKey={applicantsSort?.key} sortDirection={applicantsSort?.direction} onSortAsc={handleApplicantsSortAsc} onSortDesc={handleApplicantsSortDesc} />
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={COL_COUNT} className="inventory-items-hint inventory-table-empty-cell">
                                        Loading job profiles…
                                    </td>
                                </tr>
                            ) : applicants.length === 0 ? (
                                <tr>
                                    <td colSpan={COL_COUNT} className="inventory-items-hint inventory-table-empty-cell">
                                        No candidates available for this industry yet.
                                    </td>
                                </tr>
                            ) : sortedApplicants.length === 0 ? (
                                <tr>
                                    <td colSpan={COL_COUNT} className="inventory-items-hint inventory-table-empty-cell">
                                        No candidates match the selected filters.
                                    </td>
                                </tr>
                            ) : (
                                pagedApplicants.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <Link
                                                to={`/hiring/candidate/${row.id}`}
                                                state={{ candidate: row }}
                                                className="hiring-applicant-cell hiring-applicant-cell--link"
                                            >
                                                <div className="hiring-avatar" aria-hidden>
                                                    {getApplicantInitials(row.name)}
                                                </div>
                                                <span className="inventory-item-name-link hiring-applicant-name-link">
                                                    {row.name}
                                                </span>
                                            </Link>
                                        </td>
                                        <td>
                                            <span className="user-role-pill">{row.role}</span>
                                        </td>
                                        <td className="inventory-purchase-items-cell">
                                            {(row.preferredLocations || []).length > 0 ? (
                                                <button
                                                    type="button"
                                                    className="inventory-purchase-view-items-btn"
                                                    onClick={() => setLocationsModalApplicant(row)}
                                                >
                                                    View locations
                                                </button>
                                            ) : (
                                                <span className="hiring-locations-empty">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="hiring-status-pill">
                                                {row.status}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="hiring-exp-badge">
                                                <FontAwesomeIcon icon={faBriefcase} />
                                                +{row.experienceYears} yrs
                                            </span>
                                        </td>
                                        <td className="table-col-mobile">
                                            {row.phone ? (
                                                <a
                                                    href={`tel:${String(row.phone).replace(/\D/g, '')}`}
                                                    className="hiring-contact-link"
                                                    title={formatMobileDisplayOrFallback(row.phone, '')}
                                                >
                                                    <FontAwesomeIcon icon={faPhone} />
                                                    {formatMobileDisplayOrFallback(row.phone, '—')}
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td>
                                            {row.email ? (
                                                <a
                                                    href={`mailto:${row.email}`}
                                                    className="hiring-contact-link"
                                                    title={row.email}
                                                >
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                    {row.email}
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td>
                                            {row.resumeFile ? (
                                                row.resumeUrl ? (
                                                    <a
                                                        href={row.resumeUrl}
                                                        className="hiring-doc-link"
                                                        title={row.resumeFile}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <FontAwesomeIcon icon={faFileLines} />
                                                        {row.resumeFile}
                                                    </a>
                                                ) : (
                                                    <span
                                                        className="hiring-doc-link"
                                                        title={row.resumeFile}
                                                    >
                                                        <FontAwesomeIcon icon={faFileLines} />
                                                        {row.resumeFile}
                                                    </span>
                                                )
                                            ) : (
                                                <span className="hiring-locations-empty">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && sortedApplicants.length > 0 ? (
                    <OrdersTablePagination
                        currentPage={pageSafe}
                        totalPages={totalPages}
                        totalItems={sortedApplicants.length}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={(nextSize) => {
                            setPageSize(nextSize);
                            setPage(1);
                        }}
                        itemLabel="candidates"
                        ariaLabel="Hiring candidates pagination"
                    />
                ) : null}
            </div>

            {locationsModalApplicant ? (
                <div
                    className="inventory-modal-overlay hiring-locations-modal-overlay"
                    onClick={() => setLocationsModalApplicant(null)}
                >
                    <div
                        className="inventory-modal hiring-locations-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="hiring-locations-modal-title"
                    >
                        <div className="inventory-modal-header">
                            <h4 id="hiring-locations-modal-title">
                                Preferred locations
                            </h4>
                            <button
                                type="button"
                                className="hiring-locations-modal-close"
                                onClick={() => setLocationsModalApplicant(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <ul className="hiring-locations-modal-list">
                            {(locationsModalApplicant.preferredLocations || []).map((loc) => (
                                <li
                                    key={getHiringLocationKey(loc)}
                                    className="hiring-location-chip hiring-location-chip--modal"
                                >
                                    <span className="hiring-location-chip-zip">
                                        {formatHiringLocationPrimary(loc)}
                                    </span>
                                    <span className="hiring-location-chip-place">
                                        {formatHiringLocationSecondary(loc)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ) : null}
        </>
    );
}

const Hiring = () => {
    const entityLabel = isSupermarketContext() ? 'Grocery store' : 'Restaurant';

    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const loadApplicants = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchJobProfilesForCurrentEntity();
            setApplicants(Array.isArray(data.applicants) ? data.applicants : []);
        } catch (err) {
            setApplicants([]);
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load job profiles.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadApplicants();
    }, [loadApplicants]);

    return (
        <div className="menu inventory-page purchase-page hiring-page">
            <div className="inventory-supplier-panel hiring-panel user-management-panel">
                <div className="hiring-page-head">
                    <h2 className="hiring-page-title">{entityLabel} hiring</h2>
                </div>

                {error ? (
                    <p className="inventory-status" role="alert">
                        {error}
                    </p>
                ) : null}

                <HiringApplicantsTable
                    applicants={applicants}
                    loading={loading}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                />
            </div>
        </div>
    );
};

export default Hiring;
